import { useState } from "react";
import { Button, Input } from "@/components";
import { FormCard } from "@/components/FormCard";
import { ArrowRight, Copy, CopyCheck, X, Zap } from "lucide-react";

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export default function WhatsAppWidgetGenerator() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [brandName, setBrandName] = useState("");
  const [buttonPosition, setButtonPosition] = useState("bottom-left");
  const [welcomeText, setWelcomeText] = useState("Hello! How can we help?");
  const [prefilledMessage, setPrefilledMessage] = useState(
    "Hello! I'd like to know more about your services.",
  );
  const [brandImageUrl, setBrandImageUrl] = useState("");

  const [phoneError, setPhoneError] = useState("");
  const [message, setMessage] = useState("Chat with us 🚀");
  const [buttonColor, setButtonColor] = useState("#185db8");
  const [copied, setCopied] = useState(false);
  const [generatedCode, setGeneratedCode] = useState("");

  
  // Icon settings & live preview states
  const [iconType, setIconType] = useState<"whatsapp" | "chat">("whatsapp");
  const [isPreviewChatOpen, setIsPreviewChatOpen] = useState(true);



  const generateCode = () => {
    if (!phoneNumber.trim()) {
      setPhoneError("Phone number is required");
      return;
    }
    setPhoneError("");

    const position = buttonPosition === "bottom-left" ? "left" : "right";
    const marginRight = buttonPosition === "bottom-left" ? "0" : "20";
    const marginLeft = buttonPosition === "bottom-left" ? "20" : "0";

    const code = `<script>
  var url = 'https://quick-assists-media.s3.ap-south-1.amazonaws.com/whatsapp-wati-script/Widget.js';
  var s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = true;
  s.src = url;
  var options = {
    "enabled": true,
    "chatButtonSetting": {
      "backgroundColor": "${buttonColor}",
      "ctaText": "${message}",
      "borderRadius": "25",
      "marginLeft": "${marginLeft}",
      "marginRight": "${marginRight}",
      "marginBottom": "20",
      "ctaIconWATI": ${iconType === "chat"},
      "position": "${position}"
    },
    "brandSetting": {
      "brandName": "${brandName}",
      "brandSubTitle": "undefined",
      "brandImg": "${brandImageUrl}",
      "welcomeText": "${welcomeText.replace(/\n/g, "\\n")}",
      "messageText": "${prefilledMessage}",
      "backgroundColor": "${buttonColor}",
      "ctaText": "${message}",
      "borderRadius": "25",
      "phoneNumber": "${phoneNumber.trim()}"
    }
  };
  s.onload = function() {
    CreateWhatsappChatWidget(options);
  };
  var x = document.getElementsByTagName('script')[0];
  x.parentNode.insertBefore(s, x);
<\/script>`;

    setGeneratedCode(code);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(generatedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4">
      {/* 2-Column Responsive Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
              {/* Right Column: Settings Forms Stack (7 cols) */}
        <div className="lg:col-span-8 space-y-4">
          
          {/* Card 1: Button Style */}
          <FormCard title="Button style" description="Choose how the launcher looks and behaves">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Brand Color */}
              <div className="space-y-1.5">
                <label htmlFor="brand-color-text" className="block text-xs font-semibold text-ui-body">Brand color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={buttonColor}
                    onChange={(e) => setButtonColor(e.target.value)}
                    className="w-9 h-9 rounded-sm border border-ui-hairline cursor-pointer shrink-0"
                  />
                  <Input
                    id="brand-color-text"
                    type="text"
                    value={buttonColor}
                    onChange={(e) => setButtonColor(e.target.value)}
                    placeholder="#185db8"
                    className="flex-1 h-9 text-xs"
                  />
                </div>
              </div>

              {/* Button Label / CTA Text */}
              <div className="space-y-1.5">
                <label htmlFor="button-label" className="block text-xs font-semibold text-ui-body">Button label</label>
                <Input
                  id="button-label"
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Chat with us"
                  className="w-full h-9 text-xs"
                />
              </div>

              {/* Position */}
              <div className="space-y-2">
                <span className="block text-xs font-semibold text-ui-body">Position</span>
                <div className="flex items-center gap-1.5 p-1 bg-ui-surface-soft border border-ui-hairline rounded-sm w-fit">
                  <button
                    type="button"
                    onClick={() => setButtonPosition("bottom-left")}
                    className={cn(
                      "px-3 py-1.5 text-xs font-bold rounded-sm transition-all whitespace-nowrap cursor-pointer",
                      buttonPosition === "bottom-left"
                        ? "bg-ui-surface-card text-ui-primary border border-ui-hairline shadow-sm"
                        : "text-ui-muted hover:text-ui-ink"
                    )}
                  >
                    Bottom left
                  </button>
                  <button
                    type="button"
                    onClick={() => setButtonPosition("bottom-right")}
                    className={cn(
                      "px-3 py-1.5 text-xs font-bold rounded-sm transition-all whitespace-nowrap cursor-pointer",
                      buttonPosition === "bottom-right"
                        ? "bg-ui-surface-card text-ui-primary border border-ui-hairline shadow-sm"
                        : "text-ui-muted hover:text-ui-ink"
                    )}
                  >
                    Bottom right
                  </button>
                </div>
              </div>

                {/* Chat Button Preview / Icon Selection setting */}
              <div className="space-y-2">
                <span className="block text-xs font-semibold text-ui-body">Chat Button Preview</span>
                <div className="flex items-center gap-6">
                  {/* WhatsApp Icon Option */}
                  <label className="flex items-center gap-2 cursor-pointer group select-none">
                    <input
                      type="radio"
                      name="iconType"
                      value="whatsapp"
                      checked={iconType === "whatsapp"}
                      onChange={() => setIconType("whatsapp")}
                      className="w-4 h-4 accent-ui-primary cursor-pointer"
                    />
                    <div
                      style={{ backgroundColor: buttonColor }}
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white transition-all shadow-none group-hover:scale-105"
                    >
                      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                    </div>
                  </label>

                  {/* Chat Icon Option */}
                  <label className="flex items-center gap-2 cursor-pointer group select-none">
                    <input
                      type="radio"
                      name="iconType"
                      value="chat"
                      checked={iconType === "chat"}
                      onChange={() => setIconType("chat")}
                      className="w-4 h-4 accent-ui-primary cursor-pointer"
                    />
                    <div
                      style={{ backgroundColor: buttonColor }}
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white transition-all shadow-none group-hover:scale-105"
                    >
                      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        <circle cx="8" cy="10" r="1" fill="currentColor" />
                        <circle cx="12" cy="10" r="1" fill="currentColor" />
                        <circle cx="16" cy="10" r="1" fill="currentColor" />
                      </svg>
                    </div>
                  </label>
                </div>
              </div>


      
            </div>
          </FormCard>
<div className="grid md:grid-cols-2 gap-4">
          {/* Card 2: Business Details */}
          <FormCard title="Business details" description="Shown to visitors inside the widget">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Brand Name */}
              <div className="space-y-1.5">
                <label htmlFor="brand-name" className="block text-xs font-semibold text-ui-body">Brand name</label>
                <Input
                  id="brand-name"
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="Your company name"
                  className="w-full h-9 text-xs"
                />
              </div>



              {/* Phone Number */}
              <div className="space-y-1.5">
                <label htmlFor="phone-number" className="block text-xs font-semibold text-ui-body flex items-center justify-between">
                  Phone number
                </label>
                <Input
                  id="phone-number"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPhoneNumber(value);
                    if (value.trim()) {
                      setPhoneError("");
                    }
                  }}
                  placeholder="1234567890"
                  className="w-full h-9 text-xs"
                />
                {phoneError && (
                  <p className="text-[10px] text-ui-error font-medium">{phoneError}</p>
                )}

              </div>

            </div>
            
              {/* Logo URL */}
              <div className="mt-4 space-y-1.5">
                <label htmlFor="brand-image-url" className="block text-xs font-semibold text-ui-body">Logo URL</label>
                <Input
                  id="brand-image-url"
                  type="url"
                  value={brandImageUrl}
                  onChange={(e) => setBrandImageUrl(e.target.value)}
                  placeholder="https://yoursite.com/logo.png"
                  className="w-full h-9 text-xs"
                />
              </div>
          </FormCard>

          {/* Card 3: Messages */}
          <FormCard title="Messages" description="What visitors read and send first">
            <div className="space-y-4">
              {/* Welcome Text */}
              <div className="space-y-1.5">
                <label htmlFor="welcome-text" className="block text-xs font-semibold text-ui-body flex justify-between items-center">
                  Welcome text
                  <span className="block text-[10px] text-ui-muted font-normal mt-0.5">{welcomeText.length}/40 characters</span>
                </label>
                <Input
                  id="welcome-text"
                  type="text"
                  value={welcomeText}
                  onChange={(e) => setWelcomeText(e.target.value)}
                  placeholder="Hello! How can we help?"
                  maxLength={40}
                  className="w-full h-9 text-xs"
                />
              </div>

              {/* Pre-filled Message */}
              <div className="space-y-1.5">
                <label htmlFor="prefilled-message" className="block text-xs font-semibold text-ui-body flex justify-between items-center">
                  Pre-filled message
                  <span className="block text-[10px] text-ui-muted font-normal mt-0.5">auto-sent when a visitor opens the chat</span>
                </label>
                <textarea
                  id="prefilled-message"
                  value={prefilledMessage}
                  onChange={(e) => setPrefilledMessage(e.target.value)}
                  placeholder="Hello! I'd like to know more about your services."
                  rows={2}
                  className="w-full px-3 py-2 border border-ui-hairline rounded-sm focus:ring-1 focus:ring-ui-primary focus:border-ui-primary bg-ui-surface-card text-xs text-ui-ink resize-none custom-scrollbar"
                />
              </div>
            </div>
          </FormCard>
</div>
          {/* Action Button Row */}
          <div className="flex justify-end pt-2">
            <Button
              size="sm"
              onClick={generateCode}
              className="flex items-center gap-2 h-9 text-xs font-semibold"
            >
              Generate Widget Code
              <ArrowRight size={14} />
            </Button>
          </div>
        </div>
        {/* Left Column: Live Mockup Preview Card (5 cols) */}
        <div className="lg:col-span-4 lg:sticky lg:top-0 space-y">
          <FormCard title="Widget Live Preview" description="Interactive preview of how the widget fits on a website">
            
            {/* Simulated Browser Viewport */}
            <div className="w-full border border-ui-hairline rounded-sm overflow-hidden h-[460px] relative bg-ui-surface-soft/30 select-none">
              {/* Browser Topbar */}
              <div className="bg-ui-surface-soft border-b border-ui-hairline px-3 py-2 flex items-center gap-2">
                <div className="flex gap-1.5 shrink-0">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 bg-ui-surface-card border border-ui-hairline-soft rounded-sm px-2 py-0.5 text-[10px] text-ui-muted truncate font-mono text-center">
                  your-website.com
                </div>
              </div>

              {/* Simulated Webpage Body */}
              <div className="p-6 text-center space-y-3 mt-8">
                <h2 className="text-sm font-bold text-ui-ink tracking-tight">Your Custom Landing Page</h2>
                <p className="text-[11px] text-ui-muted max-w-[85%] mx-auto leading-relaxed">
                  This browser window shows how the floating WhatsApp Widget will display to your actual website visitors.
                </p>
                <div className="w-20 h-1.5 bg-ui-primary/10 rounded-full mx-auto" />
                <div className="space-y-1.5 pt-4">
                  <div className="w-[70%] h-2 bg-ui-surface-soft rounded-sm mx-auto" />
                  <div className="w-[50%] h-2 bg-ui-surface-soft rounded-sm mx-auto" />
                </div>
              </div>

              {/* Widget Elements absolutely positioned inside browser mockup */}
              <div
                className={cn(
                  "absolute bottom-4 z-10 flex flex-col items-end gap-3",
                  buttonPosition === "bottom-left" ? "left-4" : "right-4"
                )}
              >
                {/* Mock Open Chat Drawer */}
                {isPreviewChatOpen && (
                  <div className="w-[280px] bg-ui-surface-card border border-ui-hairline rounded-sm flex flex-col overflow-hidden shadow-none mb-1">
                    {/* Header */}
                    <div
                      style={{ backgroundColor: buttonColor }}
                      className="p-3 text-white flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        {brandImageUrl ? (
                          <img
                            src={brandImageUrl}
                            alt={brandName || "Logo"}
                            className="w-7 h-7 rounded-full border border-white/20 bg-white object-cover"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center font-bold text-xs shrink-0">
                            {(brandName || "C").charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <h4 className="text-[11px] font-bold leading-tight truncate">{brandName || "Brand Name"}</h4>
                          <p className="text-[8px] text-white/80">Typically replies instantly</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setIsPreviewChatOpen(false)}
                        className="text-white/80 hover:text-white cursor-pointer"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Chat Body */}
                    <div className="p-3 bg-ui-surface-soft/40 space-y-3 min-h-[120px] max-h-[160px] overflow-y-auto custom-scrollbar flex flex-col justify-end">
                      <div className="bg-ui-surface-card border border-ui-hairline p-2 rounded-sm text-[11px] text-ui-ink max-w-[85%] self-start leading-relaxed shadow-none">
                        {welcomeText || "Hello! How can we help?"}
                      </div>
                    </div>

                    {/* Footer with prefilled message */}
                    <div className="p-2.5 border-t border-ui-hairline bg-ui-surface-card">
                      <a
                        href={`https://wa.me/${phoneNumber || "1234567890"}?text=${encodeURIComponent(prefilledMessage)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-7 text-white rounded-sm text-[11px] font-bold flex items-center justify-center gap-1.5 transition-colors"
                        style={{ backgroundColor: buttonColor }}
                      >
                        {iconType === "whatsapp" ? (
                          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                        )}
                        Start Chat
                      </a>
                    </div>
                  </div>
                )}

                {/* Floating preview launcher button */}
                <button
                  type="button"
                  onClick={() => setIsPreviewChatOpen(!isPreviewChatOpen)}
                  style={{ backgroundColor: buttonColor }}
                  className="flex items-center gap-1.5 text-white px-3 py-1.5 rounded-full shadow-none hover:opacity-95 transition-all duration-200 cursor-pointer"
                >
                  {iconType === "whatsapp" ? (
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      <circle cx="8" cy="10" r="1" fill="currentColor" />
                      <circle cx="12" cy="10" r="1" fill="currentColor" />
                      <circle cx="16" cy="10" r="1" fill="currentColor" />
                    </svg>
                  )}
                  <span className="text-[10px] font-semibold">{message || "Chat with us"}</span>
                </button>
              </div>
            </div>

          </FormCard>
        </div>

  

      </div>

      {/* Generated Code Output */}
      {generatedCode && (
        <div className="flex flex-col justify-between w-full gap-4 bg-ui-surface-card p-4 rounded-sm border border-ui-hairline mt-4">
          <div className="flex items-center justify-between pb-2 border-b border-ui-hairline-soft">
            <h3 className="text-xs font-bold text-ui-ink">Your Generated Widget Code</h3>
            <button
              type="button"
              onClick={copyCode}
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-sm font-semibold transition-all text-xs cursor-pointer",
                copied
                  ? "bg-ui-success text-white"
                  : "bg-ui-primary text-white hover:bg-ui-primary-active"
              )}
            >
              {copied ? (
                <>
                  <CopyCheck size={14} strokeWidth={2.5} />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy size={14} />
                  <span>Copy Code</span>
                </>
              )}
            </button>
          </div>

          <pre className="text-ui-primary text-xs overflow-x-auto whitespace-pre-wrap break-all p-3 bg-ui-surface-soft border border-ui-hairline rounded-sm font-mono custom-scrollbar max-h-40">
            <code>{generatedCode}</code>
          </pre>

          <div className="rounded-sm border border-ui-warning/30 bg-ui-warning/10 p-3 text-xs text-ui-warning flex gap-2">
            <span className="font-bold">Note:</span> Paste this code before the closing &lt;/body&gt; tag on your website.
          </div>

          <div className="divider flex items-center gap-2 my-1">
            <hr className="flex-1 border-t border-ui-hairline-soft" />
            <span className="text-[10px] font-bold text-ui-muted whitespace-nowrap uppercase tracking-wider">OR</span>
            <hr className="flex-1 border-t border-ui-hairline-soft" />
          </div>

          <div className="bg-ui-primary-light rounded-sm border border-ui-hairline p-4">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="max-w-10 w-10 h-10 bg-ui-primary rounded-full flex items-center justify-center text-white shrink-0">
                  <Zap size={18} />
                </div>
                <div className="flex flex-col gap-0.5">
                  <h3 className="text-xs font-bold text-ui-ink">Automate conversations when someone clicks your link</h3>
                  <p className="text-[11px] text-ui-muted">TheQuickAssist handles instant auto-replies, lead capture, and full WhatsApp CRM.</p>
                </div>
              </div>

              <Button
                size="sm"
                className="flex items-center gap-2 font-semibold"
              >
                Start free <ArrowRight size={14} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
